import React from 'react';
import {
  TableRow,
  TableCell,
  IconButton,
  Tooltip,
  Typography,
  Button,
  CircularProgress,
  Box,
} from '@mui/material';
import { Edit, Delete, PictureAsPdf, NoteAdd, EditNote, Archive } from '@mui/icons-material';

interface Order {
  id: string;
  accessionId: string;
  patientId: string;
  collectionDatetime: string;
  specimenClass: string;
  orderingPhysician: string;
  accessionLocationId: string;
  clinicalHistory: string;
  accessionedBy: string;
  accessionedById: string;
  grossingTechId: string;
  assignedPathologistId: string;
  status: string;
  createdAt: string;
  updatedAt: string;
  amendedFromId?: number;
  amendedAt?: string;
  amendedBy?: string;
  version?: string;
  addendaId?: string;
  orderedBy?: number;
  orderDate?: string;
  createdBy?: string;
  sectionValues?: string;
}

interface Patient {
  id: string; // UUID from GraphQL
  patientId: string;
  name: string;
  dob: string;
  sex?: string;
  mrn?: string;
  locationId?: string;
  createdAt?: string;
}

interface User {
  id: number;
  name: string;
  role: string;
}

interface Location {
  location_id: number | string;
  labName: string;
}

interface OrderTableRowProps {
  order: Order;
  patient: Patient | undefined;
  location: Location | undefined;
  users: User[];
  lastOrders: { accession: string; date: string }[];
  isPathologist: boolean;
  onEdit: (order: Order) => void;
  onDelete: (id: string) => void;
  onGeneratePDF: (accessionId: string) => void;
  onAmend: (order: Order) => void;
  onAddendum: (order: Order) => void;
  onArchive: (order: Order) => void;
  onViewVersionHistory: (order: Order) => void;
  pdfGenerating: boolean;
  pdfGeneratingOrderId: string | null;
}

const OrderTableRow: React.FC<OrderTableRowProps> = React.memo(({
  order,
  patient,
  location,
  users,
  lastOrders,
  isPathologist,
  onEdit,
  onDelete,
  onGeneratePDF,
  onAmend,
  onAddendum,
  onArchive,
  onViewVersionHistory,
  pdfGenerating,
  pdfGeneratingOrderId,
}) => {
  const isGeneratingPdf = pdfGenerating && pdfGeneratingOrderId === order.id;

  return (
    <TableRow key={order.id}>
      <TableCell>{order.accessionId || 'N/A'}</TableCell>
      <TableCell>
        {patient ? (
          <Tooltip
            title={
              <Box>
                <Typography variant="body2">
                  <strong>Recent Orders:</strong>
                </Typography>
                {lastOrders.map((lastOrder, index) => (
                  <Typography key={index} variant="body2">
                    {lastOrder.accession} - {new Date(lastOrder.date).toLocaleDateString()}
                  </Typography>
                ))}
              </Box>
            }
            arrow
          >
            <Typography variant="body2" sx={{ cursor: 'pointer' }}>
              {patient.name}
            </Typography>
          </Tooltip>
        ) : (
          'N/A'
        )}
      </TableCell>
      <TableCell>
        {order.collectionDatetime
          ? new Date(order.collectionDatetime).toLocaleDateString()
          : 'N/A'}
      </TableCell>
      <TableCell>{order.specimenClass || 'N/A'}</TableCell>
      <TableCell>
        {users.find(u => u.id.toString() === order.orderingPhysician?.toString())?.name || order.orderingPhysician || 'N/A'}
      </TableCell>
      <TableCell>
        {users.find(u => u.id.toString() === order.assignedPathologistId)?.name || 'N/A'}
      </TableCell>
      <TableCell>{location?.labName || 'N/A'}</TableCell>
      <TableCell>
        <Box display="flex" alignItems="center" gap={1}>
          <Typography
            variant="body2"
            sx={{
              color: order.status === 'Completed' ? '#4caf50' : 
                     order.status === 'In Progress' ? '#2196f3' :
                     order.status === 'Held' ? '#ff9800' :
                     order.status === 'Pending QA Review' ? '#9c27b0' : '#666',
              fontWeight: 'medium'
            }}
          >
            {order.status || 'N/A'}
          </Typography>
          {order.version && (
            <Typography variant="caption" color="text.secondary">
              v{order.version}
            </Typography>
          )}
        </Box>
      </TableCell>
      <TableCell>
        <Box display="flex" gap={1}>
          {/* Show Edit and Delete only for In Progress orders */}
          {order.status === 'In Progress' && (
            <>
              <Tooltip title="Edit">
                <IconButton
                  size="small"
                  onClick={() => onEdit(order)}
                  sx={{ color: '#1976d2' }}
                >
                  <Edit />
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete">
                <IconButton
                  size="small"
                  onClick={() => onDelete(order.id)}
                  sx={{ color: '#d32f2f' }}
                >
                  <Delete />
                </IconButton>
              </Tooltip>
            </>
          )}
          
          {/* Show PDF for all orders */}
          <Tooltip title="Generate PDF">
            <IconButton
              size="small"
              onClick={() => onGeneratePDF(order.accessionId)}
              disabled={isGeneratingPdf}
              sx={{ color: '#388e3c' }}
            >
              {isGeneratingPdf ? (
                <CircularProgress size={16} />
              ) : (
                <PictureAsPdf />
              )}
            </IconButton>
          </Tooltip>
          
          {/* Show Amend and Addendum only for pathologists and completed orders */}
          {isPathologist && (order.status === 'Completed' || order.status === 'Completed / Finalized') && (
            <>
              <Tooltip title="Amend Order">
                <IconButton
                  size="small"
                  onClick={() => onAmend(order)}
                  sx={{ color: '#f57c00' }}
                >
                  <EditNote />
                </IconButton>
              </Tooltip>
              <Tooltip title="Add Addendum">
                <IconButton
                  size="small"
                  onClick={() => onAddendum(order)}
                  sx={{ color: '#7b1fa2' }}
                >
                  <NoteAdd />
                </IconButton>
              </Tooltip>
            </>
          )}
          
          {/* Show Archive for completed orders */}
          {(order.status === 'Completed' || order.status === 'Completed / Finalized') && (
            <Tooltip title="Archive Order">
              <IconButton
                size="small"
                onClick={() => onArchive(order)}
                sx={{ color: '#9c27b0' }}
              >
                <Archive />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </TableCell>
    </TableRow>
  );
});

OrderTableRow.displayName = 'OrderTableRow';

export default OrderTableRow; 